import React, { useState, useEffect } from "react";
import { Sun, ShieldCheck, Loader2, Users, Clock, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "./ui/dialog";
import { useToast } from "../hooks/use-toast";
import { createSolarCheckout, checkSolarReferral } from "../lib/api";
import { solarPackages } from "../mock";
import { CAMPAIGN_DISCOUNT_PERCENT, CAMPAIGN_DISCOUNT_RATE, CAMPAIGN_LABEL } from "../lib/promotion";

const fmt = (n) => n.toLocaleString("sv-SE");
const parseKr = (s) => parseInt(String(s).replace(/\D/g, ""), 10) || 0;

export function SolarCheckout({ pkgIndex, triggerClassName, triggerLabel, triggerVariant, referrer, open: controlledOpen, onOpenChange: controlledOnOpenChange, hideTrigger }) {
  const pkg = solarPackages[pkgIndex];
  const packageId = `solar_${pkgIndex + 1}`;
  const base = parseKr(pkg.price);
  const { toast } = useToast();

  const [openState, setOpenState] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : openState;
  const setOpen = isControlled ? (controlledOnOpenChange || (() => {})) : setOpenState;
  const [loading, setLoading] = useState(false);
  const [terms, setTerms] = useState(false);
  const [ref, setRef] = useState({ valid: false, discount: 0.10, inviter: "" });
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "", address: "", zip: "", city: "",
  });

  useEffect(() => {
    let cancelled = false;
    if (referrer) {
      checkSolarReferral(referrer)
        .then((d) => { if (!cancelled && d?.valid) setRef({ valid: true, discount: d.discount || 0.10, inviter: d.inviter || "" }); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [referrer]);

  const referralDiscount = ref.valid ? ref.discount : 0;
  const discount = Math.max(CAMPAIGN_DISCOUNT_RATE, referralDiscount);
  const final = Math.round(base * (1 - discount));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const errToMsg = (err) => {
    const d = err?.response?.data?.detail;
    if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(", ");
    if (typeof d === "string") return d;
    return "Kunde inte starta betalningen. Försök igen.";
  };

  const handlePay = async () => {
    if (!form.firstName || !form.lastName || !form.email || !form.phone || !form.address) {
      toast({ title: "Fyll i alla obligatoriska fält", description: "Namn, e-post, telefon och adress krävs." });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast({ title: "Ogiltig e-postadress", description: "Kontrollera att e-postadressen är korrekt." });
      return;
    }
    if (!terms) {
      toast({ title: "Godkänn avtalsvillkoren", description: "Du måste godkänna det 2-åriga avtalet för att betala." });
      return;
    }
    setLoading(true);
    try {
      const { checkout_url } = await createSolarCheckout({
        package_id: packageId,
        referrer: ref.valid ? referrer : null,
        acceptedTerms: terms,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        address: form.address,
        zip: form.zip || null,
        city: form.city || null,
        origin_url: window.location.origin,
        promotion: CAMPAIGN_LABEL,
      });
      window.location.href = checkout_url;
    } catch (err) {
      setLoading(false);
      toast({ variant: "destructive", title: "Något gick fel", description: errToMsg(err) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button data-testid={`solar-buy-${packageId}`} variant={triggerVariant} className={triggerClassName}>
            {triggerLabel || `Boka & betala`}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="light-card bg-white max-w-lg max-h-[92vh] overflow-y-auto" data-testid="solar-checkout-dialog">
        <DialogHeader>
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#1B45C4]/10">
            <Sun className="w-6 h-6 text-[#1B45C4]" />
          </div>
          <DialogTitle className="text-slate-900 text-xl">{pkg.name} · Solcellstvätt</DialogTitle>
          <DialogDescription className="text-slate-500">{pkg.panels} · fast pris inkl. moms & ROT</DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2.5 rounded-xl bg-[#38BDF8]/10 ring-1 ring-[#38BDF8]/40 p-3.5" data-testid="solar-campaign-banner">
          <Sun className="w-5 h-5 text-[#1B45C4] shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700"><strong className="text-[#1B45C4]">{CAMPAIGN_DISCOUNT_PERCENT}% kampanjrabatt</strong> är redan avdragen. Den högsta tillämpliga rabatten gäller.</p>
        </div>
        {ref.valid && (
          <div className="flex items-start gap-2.5 rounded-xl bg-[#38BDF8]/10 ring-1 ring-[#38BDF8]/40 p-3.5" data-testid="solar-referral-banner">
            <Users className="w-5 h-5 text-[#1B45C4] shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700">
              {ref.inviter ? <><strong>{ref.inviter}</strong> har bjudit in dig!</> : "Du är inbjuden av en granne!"}{" "}
              Du får <strong className="text-[#1B45C4]" data-testid="solar-referral-pct">{Math.round(Math.max(CAMPAIGN_DISCOUNT_RATE, ref.discount) * 100)}% rabatt</strong> direkt.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-1">
            <Label htmlFor="sc-fn" className="text-slate-700 text-xs font-semibold">Förnamn *</Label>
            <Input id="sc-fn" data-testid="solar-firstname" value={form.firstName} onChange={set("firstName")} className="mt-1" />
          </div>
          <div className="col-span-1">
            <Label htmlFor="sc-ln" className="text-slate-700 text-xs font-semibold">Efternamn *</Label>
            <Input id="sc-ln" data-testid="solar-lastname" value={form.lastName} onChange={set("lastName")} className="mt-1" />
          </div>
          <div className="col-span-1">
            <Label htmlFor="sc-em" className="text-slate-700 text-xs font-semibold">E-post *</Label>
            <Input id="sc-em" type="email" data-testid="solar-email" value={form.email} onChange={set("email")} className="mt-1" />
          </div>
          <div className="col-span-1">
            <Label htmlFor="sc-ph" className="text-slate-700 text-xs font-semibold">Telefon *</Label>
            <Input id="sc-ph" data-testid="solar-phone" value={form.phone} onChange={set("phone")} className="mt-1" />
          </div>
          <div className="col-span-2">
            <Label htmlFor="sc-ad" className="text-slate-700 text-xs font-semibold">Adress *</Label>
            <Input id="sc-ad" data-testid="solar-address" value={form.address} onChange={set("address")} className="mt-1" />
          </div>
          <div className="col-span-1">
            <Label htmlFor="sc-zip" className="text-slate-700 text-xs font-semibold">Postnr</Label>
            <Input id="sc-zip" data-testid="solar-zip" value={form.zip} onChange={set("zip")} className="mt-1" />
          </div>
          <div className="col-span-1">
            <Label htmlFor="sc-city" className="text-slate-700 text-xs font-semibold">Ort</Label>
            <Input id="sc-city" data-testid="solar-city" value={form.city} onChange={set("city")} className="mt-1" />
          </div>
        </div>

        {/* Time info */}
        <div className="flex items-start gap-2.5 rounded-xl bg-[#f0f7ff] p-3.5 text-sm text-slate-600">
          <Clock className="w-5 h-5 text-[#1B45C4] shrink-0 mt-0.5" />
          <span>Solcellstvätt tar ca <strong>1–2 timmar</strong>. Vi hör av oss och bokar in en tid som passar dig.</span>
        </div>

        {/* Terms */}
        <div className="rounded-xl bg-slate-50 ring-1 ring-slate-100 p-3.5 text-xs text-slate-600 leading-relaxed">
          <strong className="text-slate-800">Avtalsvillkor:</strong> Abonnemanget är bindande i 2 år med 2 tvättar per år.
          Du betalar år 1 nu – år 2 faktureras separat inför säsongen. Avtalet måste sägas upp senast 1 månad före
          sista årets slut, annars förlängs det automatiskt med 2 nya år.
        </div>
        <label className="flex items-start gap-3 cursor-pointer" data-testid="solar-terms-label">
          <Checkbox checked={terms} onCheckedChange={(v) => setTerms(!!v)} data-testid="solar-terms-checkbox" className="mt-0.5" />
          <span className="text-sm text-slate-700">Jag godkänner det 2-åriga bindande avtalet och betalningsvillkoren.</span>
        </label>

        {/* Total + pay */}
        <div className="flex items-center justify-between rounded-xl bg-slate-900 px-4 py-3 text-white">
          <div>
            <p className="text-xs text-white/60">Att betala nu (år 1)</p>
            <p className="text-xs text-white/50 line-through">Ord. {fmt(base)} kr</p>
          </div>
          <p className="text-2xl font-extrabold" data-testid="solar-total">{fmt(final)} kr</p>
        </div>
        <Button onClick={handlePay} disabled={loading} data-testid="solar-pay-btn"
          className="w-full rounded-full h-12 bg-[#1B45C4] hover:bg-[#12308f] text-white font-bold text-base">
          {loading ? <><Loader2 className="mr-2 w-5 h-5 animate-spin" /> Öppnar betalning…</> : <><ShieldCheck className="mr-2 w-5 h-5" /> Betala {fmt(final)} kr säkert</>}
        </Button>
        <Button type="button" variant="ghost" onClick={(event) => { event.stopPropagation(); setOpen(false); }} data-testid={`solar-back-to-packages-${packageId}`} className="w-full text-slate-600 hover:bg-slate-100 hover:text-slate-900">
          <ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka till paket
        </Button>
        <p className="text-center text-xs text-slate-400">Säker betalning via Stripe · kort</p>
      </DialogContent>
    </Dialog>
  );
}
